import { toFullName } from '@repo/toolkit/to-full-name'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'

import {
  ContactEnum,
  contactEnumMapping
} from '../../../../lib/types/enums/enrollment/contact.enum'
import { getSelectedEnumOption } from '../../../../lib/utils/functions/misc.functions'
import { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../../elements/inputs/controlled-phone-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import { ContactInformationInput } from '../../enrollment-profile/shared/contact-information-form'

const ContactEnrollmentForm = () => {
  const form = useFormContext<ContactInformationInput>()

  const parent1FirstName = form.watch('parent1FirstName')
  const parent1LastName = form.watch('parent1LastName')
  const parent1Name = toFullName(parent1FirstName, parent1LastName)
  const parent1PhoneNumber = form.watch('parent1PhoneNumber')

  const parent2FirstName = form.watch('parent2FirstName')
  const parent2LastName = form.watch('parent2LastName')
  const parent2Name = toFullName(parent2FirstName, parent2LastName)
  const parent2PhoneNumber = form.watch('parent2PhoneNumber')

  const isParent1Available = !!(
    parent1Name?.trim() &&
    !!parent1PhoneNumber &&
    isValidPhoneNumber(parent1PhoneNumber?.trim())
  )
  const isParent2Available = !!(
    parent2Name?.trim() &&
    !!parent2PhoneNumber &&
    isValidPhoneNumber(parent2PhoneNumber?.trim())
  )

  const personToContactInCaseOfEmergency = form.watch('personToContactInCaseOfEmergency')

  const clearFields = () => {
    form.setValue('personToContactInCaseOfEmergencyFullName', '')
    form.setValue('personToContactInCaseOfEmergencyPhoneNumber', '')
  }

  useEffect(() => {
    if (personToContactInCaseOfEmergency === ContactEnum.PARENT_1) {
      if (isParent1Available) {
        form.setValue('personToContactInCaseOfEmergencyFullName', parent1Name)
        form.setValue('personToContactInCaseOfEmergencyPhoneNumber', parent1PhoneNumber)
      } else {
        form.setValue('personToContactInCaseOfEmergency', undefined)
        clearFields()
      }
    } else if (personToContactInCaseOfEmergency === ContactEnum.PARENT_2) {
      if (isParent2Available) {
        form.setValue('personToContactInCaseOfEmergencyFullName', parent2Name)
        form.setValue('personToContactInCaseOfEmergencyPhoneNumber', parent2PhoneNumber)
      } else {
        form.setValue('personToContactInCaseOfEmergency', undefined)
        clearFields()
      }
    }
  }, [
    personToContactInCaseOfEmergency,
    parent1Name,
    parent1PhoneNumber,
    parent2Name,
    parent2PhoneNumber
  ])

  return (
    <div className="flex flex-col gap-2">
      <FormField
        control={form.control}
        name="personToContactInCaseOfEmergency"
        render={({ field }) => (
          <FormItem className="flex-1">
            <FormControl>
              <ControlledSelectInput
                {...field}
                required
                label={`Personne à contacter en cas d'urgence`}
                options={Object.values(ContactEnum)
                  .filter((c) => {
                    if (c === ContactEnum.PARENT_1) return isParent1Available
                    if (c === ContactEnum.PARENT_2) return isParent2Available
                    return true
                  })
                  .map((contact) => ({
                    label: contactEnumMapping[contact],
                    value: contact
                  }))}
                selectedOption={getSelectedEnumOption(field.value, contactEnumMapping)}
                onChange={(value) => {
                  field.onChange(value)
                  if (value === ContactEnum.OTHER) clearFields()
                }}
              />
            </FormControl>
          </FormItem>
        )}
      />

      {personToContactInCaseOfEmergency === ContactEnum.OTHER && (
        <div className="flex gap-2">
          <FormField
            control={form.control}
            name="personToContactInCaseOfEmergencyFullName"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} required label={'Prénom et NOM'} type="text" />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="personToContactInCaseOfEmergencyPhoneNumber"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledPhoneInput
                    {...field}
                    required
                    label="Numéro de téléphone"
                    defaultCountry="FR"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  )
}

export default ContactEnrollmentForm
